import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, Check, Circle } from 'lucide-react';
import { AccesoSocial } from '../components/AccesoSocial';
import { Logo } from '../components/Logo';
import { mensajeErrorAuth } from '../utils/authErrores';
import { comprobarContrasena, RequisitoContrasena } from '../utils/requisitosContrasena';

export const Signup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [requisitos, setRequisitos] = useState<RequisitoContrasena[]>([]);
  const { signup } = useAuth();
  const navigate = useNavigate();
  // Adónde volver al entrar: lo que pidió PrivateRoute, o el inicio
  const desde: string = (useLocation().state as { desde?: string } | null)?.desde ?? '/';
  const vieneDeInvitacion = desde.startsWith('/amedias/unirse/') || desde.startsWith('/tareas/unirse/');

  // Los requisitos se ven desde el principio y se van marcando al escribir
  useEffect(() => {
    let vigente = true;
    comprobarContrasena(password).then(r => { if (vigente) setRequisitos(r); });
    return () => { vigente = false; };
  }, [password]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    const pendientes = (await comprobarContrasena(password)).filter(r => !r.cumple);
    if (pendientes.length) {
      setError(`A la contraseña le falta: ${pendientes.map(r => r.texto.toLowerCase()).join(', ')}.`);
      return;
    }

    setLoading(true);

    try {
      await signup(email, password);
      navigate(desde, { replace: true });
    } catch (err: any) {
      setError(mensajeErrorAuth(err, 'Error al registrarse'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-8">
        <div className="flex justify-center mb-8">
          <Logo size={64} sobreClaro />
        </div>

        <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">Crear Cuenta</h1>
        <p className="text-center text-gray-600 mb-8">Comienza a gestionar tus gastos</p>

        {vieneDeInvitacion && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg mb-6 text-sm">
            Crea tu cuenta y entrarás directamente {desde.startsWith('/tareas/') ? 'a la casa a la que' : 'al plan al que'} te han invitado.
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <AccesoSocial desde={desde} onError={setError} />

        <form onSubmit={handleSignup} className="space-y-6 mt-4">
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="email"
                autoComplete="email" autoCapitalize="none" enterKeyHint="next"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="tu@email.com"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-700 font-semibold mb-2">Contraseña</label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="password"
                autoComplete="new-password" enterKeyHint="next"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="••••••••"
                aria-describedby="requisitos-contrasena"
                required
              />
            </div>
            {requisitos.length > 0 && (
              <ul id="requisitos-contrasena" className="mt-2 space-y-1 text-sm" aria-live="polite">
                {requisitos.map(r => (
                  <li key={r.texto} className={`flex items-center gap-2 ${r.cumple ? 'text-emerald-700' : 'text-gray-500'}`}>
                    {r.cumple
                      ? <Check className="w-4 h-4 shrink-0" aria-hidden />
                      : <Circle className="w-3 h-3 mx-0.5 shrink-0" aria-hidden />}
                    <span>{r.texto}<span className="sr-only">{r.cumple ? ': cumplido' : ': falta'}</span></span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <label className="block text-gray-700 font-semibold mb-2">Confirmar Contraseña</label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="password"
                autoComplete="new-password" enterKeyHint="go"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white font-semibold py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
          >
            {loading ? 'Registrando...' : 'Registrarse'}
          </button>
        </form>

        <p className="text-center text-gray-600 mt-6">
          ¿Ya tienes cuenta?{' '}
          <button
            onClick={() => navigate('/login', { state: { desde } })}
            className="text-blue-600 font-semibold hover:underline"
          >
            Inicia sesión
          </button>
        </p>
        <p className="text-center text-xs text-gray-500 mt-3">
          Al crear la cuenta, tus datos se tratan como explica la{' '}
          <a href="/privacidad" className="underline hover:text-gray-700">política de privacidad</a>.
        </p>
      </div>
    </div>
  );
};
